/**
 * PDF export pipeline for report pages.
 *
 * Pipeline: collect page DOMs -> preprocess clones -> html2canvas 4x -> JPEG -> jsPDF A4.
 *
 * The caller is responsible for capturing the 3D viewport snapshot before
 * invoking `exportReportToPdf` (the snapshot data URL is passed as an option).
 */

import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

// A4 dimensions in mm
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

export interface PdfExportOptions {
    /** Data URL of the composited 3D viewport snapshot (WebGL + overlay). */
    viewportSnapshotUrl?: string;
    /** Output file name (without .pdf extension). */
    fileName?: string;
    /** html2canvas scale factor. Defaults to 4. */
    scale?: number;
    /** JPEG quality 0–1. Defaults to 0.98. */
    jpegQuality?: number;
}

/**
 * Collect all report page elements from the DOM, ordered by page number.
 */
function collectPageElements(): HTMLElement[] {
    const pages = document.querySelectorAll<HTMLElement>('[data-report-page]');
    return Array.from(pages).sort((a, b) => Number(a.dataset.reportPage) - Number(b.dataset.reportPage));
}

/**
 * Pre-process a cloned page element so html2canvas can rasterize it faithfully.
 *
 * 1. Replace the VTK viewport container content with a single snapshot <img>.
 * 2. Remove elements marked with `data-no-pdf` (e.g. interactive buttons).
 * 3. Replace <input> elements with <span> showing the current value.
 * 4. Convert CSS background-image divs to real <img> elements.
 */
function preprocessClone(clone: HTMLElement, original: HTMLElement, viewportSnapshotUrl?: string): void {
    // ── 1. Replace VTK viewport container content with snapshot image ────
    // VTK injects multiple children (canvas, overlay canvas, bgImage, wrapper divs)
    // into the container. Replace the entire content with a single <img>.
    const viewportContainer = clone.querySelector<HTMLElement>('[data-report-viewport]');
    if (viewportContainer) {
        viewportContainer.innerHTML = '';
        if (viewportSnapshotUrl) {
            const img = document.createElement('img');
            img.src = viewportSnapshotUrl;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            viewportContainer.appendChild(img);
        }
    }

    // ── 2. Remove elements marked as non-printable ──────────────────────
    const noPdfElements = clone.querySelectorAll('[data-no-pdf]');
    noPdfElements.forEach((el) => el.remove());

    // ── 3. Replace input → span ─────────────────────────────────────────
    // Read values from original DOM since clone inputs lose their live values
    const originalInputs = original.querySelectorAll('input');
    const cloneInputs = clone.querySelectorAll('input');
    cloneInputs.forEach((input, index) => {
        const span = document.createElement('span');
        const computed = window.getComputedStyle(originalInputs[index] ?? input);
        span.style.display = 'block';
        span.style.padding = computed.padding;
        span.style.fontSize = computed.fontSize;
        span.style.fontFamily = computed.fontFamily;
        span.style.color = computed.color;
        span.style.lineHeight = computed.lineHeight;
        span.textContent = originalInputs[index]?.value ?? input.value;
        input.replaceWith(span);
    });

    // ── 4. Convert background-image divs → img ─────────────────────────
    const allDivs = clone.querySelectorAll('div');
    allDivs.forEach((div) => {
        const bg = div.style.backgroundImage;
        if (bg && bg.startsWith('url(')) {
            const url = bg.slice(4, -1).replace(/["']/g, '');
            const img = document.createElement('img');
            img.src = url;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            div.style.backgroundImage = 'none';
            div.appendChild(img);
        }
    });
}

/**
 * Rasterize a single page element to a JPEG data URL.
 */
async function rasterizePage(
    pageEl: HTMLElement,
    viewportSnapshotUrl: string | undefined,
    scale: number,
    jpegQuality: number
): Promise<string> {
    // Clone the page so preprocessing doesn't affect the live DOM
    const clone = pageEl.cloneNode(true) as HTMLElement;
    preprocessClone(clone, pageEl, viewportSnapshotUrl);

    // Temporarily mount the clone off-screen for html2canvas
    clone.style.position = 'fixed';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    document.body.appendChild(clone);

    try {
        const canvas = await html2canvas(clone, {
            scale,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
        });

        return canvas.toDataURL('image/jpeg', jpegQuality);
    } finally {
        clone.remove();
    }
}

/**
 * Export the report pages to a downloadable PDF file.
 */
export async function exportReportToPdf(options: PdfExportOptions = {}): Promise<void> {
    const { viewportSnapshotUrl, fileName = 'Report', scale = 4, jpegQuality = 0.98 } = options;

    const pages = collectPageElements();
    if (pages.length === 0) {
        throw new Error('No report pages found in the DOM');
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();

        const dataUrl = await rasterizePage(pages[i], viewportSnapshotUrl, scale, jpegQuality);

        // Compute dimensions to fill A4 width, preserving aspect ratio
        const img = await loadImage(dataUrl);
        const aspectRatio = img.height / img.width;
        const pdfWidth = A4_WIDTH_MM;
        const pdfHeight = pdfWidth * aspectRatio;

        // Center vertically if the page image is shorter than A4
        const yOffset = pdfHeight < A4_HEIGHT_MM ? (A4_HEIGHT_MM - pdfHeight) / 2 : 0;

        pdf.addImage(dataUrl, 'JPEG', 0, yOffset, pdfWidth, pdfHeight, undefined, 'NONE');
    }

    pdf.save(`${fileName}.pdf`);
}

/** Load an image from a data URL to read its dimensions. */
function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}
